<template>
  <div class="relative h-max w-full" :style="{ aspectRatio }">
    <video
      ref="videoRef"
      :controls="controls"
      class="controls-hide-download size-full"
      preload="auto"
      :autoplay="autoplay"
      muted
      @play="onVideoPlay"
      @pause="onVideoPause"
      @ended="onVideoEnd"
      @click="handleVideoClick"
    >
      <source :src="src" type="video/mp4" />
    </video>

    <img
      v-if="showPlaceholder && placeholderSrc"
      :src="placeholderSrc"
      :alt="placeholderAlt"
      class="pointer-events-none absolute inset-0 size-full object-cover transition-opacity"
      :class="{ 'opacity-0': isPlaying }"
    />

    <button
      class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold px-4 py-2 font-bold text-white shadow-lg transition-all duration-75 hover:brightness-95 active:scale-95"
      :class="{ 'scale-0': isPlaying }"
      @click="togglePlay(!isPlaying)"
    >
      <svg
        class="size-6 xl:h-8 xl:w-10"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M8 5v14l11-7z"
        />
      </svg>
    </button>
  </div>
</template>

<script lang="ts">
export default defineComponent({
  name: 'UiVideo',
  props: {
    src: {
      type: String,
      required: true,
    },
    controls: {
      type: Boolean,
      default: false,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    aspectRatio: {
      type: String,
      default: '16/9',
    },
    placeholderSrc: {
      type: String,
      default: '',
    },
    placeholderAlt: {
      type: String,
      default: 'Video Placeholder',
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const isPlaying = ref(false)
    const videoRef = ref<HTMLVideoElement | null>(null)
    const showPlaceholder = ref(true)

    const onVideoPlay = () => {
      if (!props.autoplay && videoRef.value) {
        // for autoplay, we need to mute the video first and then unmute it
        videoRef.value.muted = false
      }
      isPlaying.value = true
    }

    const onVideoPause = () => {
      isPlaying.value = false
    }

    const onVideoEnd = () => {
      // workaround for looping video (videoRef can be null if the element
      // unmounted during navigation before the loop handler fires — T13)
      if (!props.loop || !videoRef.value) {
        return
      }
      videoRef.value.currentTime = 0
      videoRef.value.play()
    }

    const togglePlay = (play: boolean) => {
      if (play) {
        videoRef.value?.play()
        showPlaceholder.value = false
      } else {
        videoRef.value?.pause()
      }
    }

    const handleVideoClick = () => {
      togglePlay(!isPlaying.value)
    }

    return {
      isPlaying,
      videoRef,
      onVideoPlay,
      onVideoPause,
      onVideoEnd,
      togglePlay,
      handleVideoClick,
      showPlaceholder,
    }
  },
})
</script>
